import { useCallback, useEffect, useMemo, useState } from "react";
import { buildQuery, normalizePropertyCard } from "../mobileUtils";

const PAGE_SIZE = 12;
const RESPONSE_CACHE_TTL_MS = 60 * 1000;
const responseCache = new Map();

const normalizeText = (value) => String(value || "").trim();
const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getCachedResponse = (query) => {
  const cached = responseCache.get(query);
  if (!cached) return null;
  if (Date.now() - cached.at > RESPONSE_CACHE_TTL_MS) {
    responseCache.delete(query);
    return null;
  }
  return cached.value;
};

const setCachedResponse = (query, value) => {
  responseCache.set(query, { at: Date.now(), value });
  if (responseCache.size > 24) {
    const firstKey = responseCache.keys().next().value;
    if (firstKey) responseCache.delete(firstKey);
  }
};

const asPayloadRows = (payload) =>
  Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.properties)
    ? payload.properties
    : Array.isArray(payload)
    ? payload
    : [];

const splitTerms = (value) =>
  normalizeText(value)
    .toLowerCase()
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const rowLocationText = (row = {}) =>
  [
    row.title,
    row.name,
    row.city,
    row.area,
    row.state,
    row.country,
    row.location,
    row.property_type,
    row.type,
  ]
    .map((value) => normalizeText(value).toLowerCase())
    .filter(Boolean)
    .join(" ");

const rowPrice = (row = {}) =>
  toNumber(
    row.display_price ||
      row.display_sell_price ||
      row.sell_price_per_night ||
      row.price_per_night ||
      row.base_price_per_night ||
      row.provider_base_price,
    0
  );

const filterCatalogRows = (rows = [], parsed = {}) => {
  const destination = normalizeText(parsed.destination || parsed.location || parsed.search || parsed.city);
  const terms = splitTerms(destination);
  const guests = toNumber(parsed.guests, 0) || toNumber(parsed.adults, 0) + toNumber(parsed.children, 0);
  const minPrice = toNumber(parsed.minPrice, 0);
  const maxPrice = toNumber(parsed.maxPrice, 0);
  const type = normalizeText(parsed.type).toLowerCase();
  const beds = toNumber(parsed.beds, 0);
  const ratingMin = toNumber(parsed.ratingMin, 0);

  return rows.filter((row) => {
    const haystack = rowLocationText(row);
    if (terms.length && !terms.some((term) => haystack.includes(term))) return false;
    if (guests && toNumber(row.max_guests || row.guests, 0) && toNumber(row.max_guests || row.guests, 0) < guests) {
      return false;
    }
    if (type && !normalizeText(row.property_type || row.type || row.category).toLowerCase().includes(type)) return false;
    if (beds && toNumber(row.bedrooms || row.beds, 0) < beds) return false;
    if (ratingMin && toNumber(row.avg_rating || row.rating, 0) < ratingMin) return false;
    const price = rowPrice(row);
    if (minPrice && price && price < minPrice) return false;
    if (maxPrice && price && price > maxPrice) return false;
    return true;
  });
};

const sortCatalogRows = (rows = [], sort = "") => {
  const list = [...rows];
  const key = normalizeText(sort).toLowerCase();
  if (key === "price_asc") return list.sort((a, b) => rowPrice(a) - rowPrice(b));
  if (key === "price_desc") return list.sort((a, b) => rowPrice(b) - rowPrice(a));
  if (key === "rating") {
    return list.sort((a, b) => toNumber(b.avg_rating || b.rating, 0) - toNumber(a.avg_rating || a.rating, 0));
  }
  return list;
};

const buildCatalogQuery = (parsed = {}, limit = 320) => {
  const destination = normalizeText(parsed.destination || parsed.location || parsed.search || parsed.city);
  return buildQuery({
    limit,
    page: 1,
    ...(destination ? { destination } : {}),
    ...(destination ? { location: destination } : {}),
    ...(destination ? { city: destination } : {}),
    ...(parsed.location_type ? { location_type: parsed.location_type } : {}),
    ...(parsed.locationType ? { locationType: parsed.locationType } : {}),
    ...(parsed.adults ? { adults: parsed.adults } : {}),
    ...(parsed.children ? { children: parsed.children } : {}),
    ...(parsed.guests ? { guests: parsed.guests } : {}),
    ...(parsed.checkIn ? { checkIn: parsed.checkIn } : {}),
    ...(parsed.checkOut ? { checkOut: parsed.checkOut } : {}),
  });
};

const mergeRows = (...groups) => {
  const map = new Map();
  groups.flat().forEach((row, index) => {
    if (!row) return;
    const key = String(
      row.provider_property_id ||
        row.providerPropertyId ||
        row.elivaas_id ||
        row.external_id ||
        row.id ||
        row.slug ||
        `${row.title || row.name || "row"}-${index}`
    );
    if (!map.has(key)) map.set(key, row);
  });
  return Array.from(map.values());
};

export default function useMobileProperties(params = {}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const key = useMemo(() => JSON.stringify(params), [params]);
  const query = useMemo(() => {
    let parsed = {};
    try {
      parsed = JSON.parse(key);
    } catch (_) {
      parsed = {};
    }
    const destinationRaw = parsed.destination || parsed.location || parsed.search || "";
    const locationType = String(parsed.location_type || parsed.locationType || "").toLowerCase();
    const destination =
      locationType === "state"
        ? destinationRaw
        : String(destinationRaw || "").split(",")[0].trim() || destinationRaw;
    return buildQuery({ limit: PAGE_SIZE, page, ...parsed, ...(destination ? { destination } : {}) });
  }, [key, page]);

  useEffect(() => {
    setPage(1);
  }, [key]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const load = async () => {
      const parsedParams = JSON.parse(key || "{}");
      const catalogCacheKey = `mobile-catalog:${key}`;
      const cachedCatalog = getCachedResponse(catalogCacheKey);
      if (cachedCatalog?.rows) {
        const offset = (page - 1) * PAGE_SIZE;
        const pageRows = cachedCatalog.rows.slice(offset, offset + PAGE_SIZE);
        const nextItems = pageRows.map(normalizePropertyCard);
        setItems((current) => (page === 1 ? nextItems : [...current, ...nextItems]));
        setTotal(Number(cachedCatalog.total || cachedCatalog.rows.length) || 0);
      }
      if (cachedCatalog?.rows) {
        setLoading(false);
        setLoadingMore(false);
        return;
      }
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError("");
      try {
        const applyRows = (filteredRows, totalCount = filteredRows.length, shouldCache = false) => {
          const offset = (page - 1) * PAGE_SIZE;
          const pageRows = filteredRows.slice(offset, offset + PAGE_SIZE);
          const nextItems = pageRows.map(normalizePropertyCard);
          setItems((current) => (page === 1 ? nextItems : [...current, ...nextItems]));
          setTotal(totalCount);
          if (shouldCache) setCachedResponse(catalogCacheKey, { rows: filteredRows, total: totalCount });
        };

        const sourceRows = { snapshot: [], direct: [], api: [] };
        let published = false;
        const publish = (shouldCache = false) => {
          if (!active) return;
          const mergedRows = sortCatalogRows(
            filterCatalogRows(mergeRows(sourceRows.snapshot, sourceRows.direct, sourceRows.api), parsedParams),
            parsedParams.sort
          );
          if (!mergedRows.length) return;
          published = true;
          applyRows(mergedRows, mergedRows.length, shouldCache);
          setLoading(false);
        };

        const snapshotPromise = fetch(`/provider-catalog/elivaas.json`, {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        })
          .then((response) => (response.ok ? response.json() : null))
          .then((payload) => {
            sourceRows.snapshot = filterCatalogRows(asPayloadRows(payload), parsedParams);
            publish(false);
          });

        const fetchPublicSource = (source) => {
          const qs = `${buildCatalogQuery(parsedParams)}&source=${source}`;
          return fetch(`/api/public/properties?${qs}`, {
            credentials: "include",
            headers: { Accept: "application/json" },
            signal: controller.signal,
          })
            .then((response) => (response.ok ? response.json() : null))
            .then((payload) => {
              sourceRows[source] = asPayloadRows(payload);
              publish(false);
            });
        };

        await Promise.allSettled([snapshotPromise, fetchPublicSource("direct"), fetchPublicSource("api")]);
        if (!active) return;
        const finalRows = sortCatalogRows(
          filterCatalogRows(mergeRows(sourceRows.snapshot, sourceRows.direct, sourceRows.api), parsedParams),
          parsedParams.sort
        );
        if (finalRows.length) {
          applyRows(finalRows, finalRows.length, true);
          return;
        }
        if (!published) throw new Error("Unable to load stays.");
      } catch (err) {
        if (!active) return;
        if (err?.name === "AbortError") return;
        setError(err?.message || "Unable to load stays.");
        if (page === 1) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (active) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    };
    load();
    return () => {
      active = false;
      controller.abort();
    };
  }, [key, page, query]);

  const hasMore = Boolean(total) && total > items.length;
  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    setPage((current) => current + 1);
  }, [hasMore, loading, loadingMore]);

  return { items, total, page, loading, loadingMore, error, hasMore, loadMore };
}
